import { CreditCardIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Base } from "@/atoms-react/text/Text.react";
import { Tooltip } from "@/atoms-react/tooltip/Tooptip.react.tsx";
import type { AliasIBAN } from "@/features/user/types";
import { Themes } from "@/types/theme.ts";
import { AliasCard } from "../alias-card/index.react";

type IbanCardProps = {
  iban: AliasIBAN;
  onClick?: () => void;
  onDelete?: () => void;
  onPublicClick?: () => void;
  onClickHandleVerify?: (iban: string) => void;
  loading?: boolean;
  isDisabledValidationButton?: boolean;
  disabled?: boolean;
  dataCy?: string;
} & Omit<React.ComponentPropsWithoutRef<"div">, "onClick">;

export const IbanCard = ({
  iban,
  loading = false,
  onClickHandleVerify,
  isDisabledValidationButton,
  disabled = false,
  ...props
}: IbanCardProps) => {
  const { t } = useTranslation(["pass"]);
  return (
    <>
      <AliasCard
        tooltipDesignation={t("olkypass.aliasPublicPage.this_iban")}
        icon={CreditCardIcon}
        isPublic={iban?.Public}
        isMain={iban.Main}
        {...props}
      >
        <>
          {iban.Main && (
            <Base
              className={twMerge(
                "flex overflow-hidden text-ellipsis break-normal font-semibold md:mr-3"
              )}
            >
              {t("atoms.iban_card.main.title")}
              <div data-tooltip={t("atoms.iban_card.main.description")} data-position="bottom">
                <InformationCircleIcon className="mr-1 h-4 w-4" />
              </div>
              {":"}
            </Base>
          )}
          <Base
            className={twMerge("overflow-hidden text-ellipsis break-normal font-normal md:mr-3")}
            title={`${iban.BankInfoDto.Institution}`}
          >
            {`${iban.BankInfoDto.Institution}`}
          </Base>
          <Base
            className={twMerge("overflow-hidden text-ellipsis break-normal font-semibold md:mr-3")}
            title={`${iban.Iban}`}
          >
            {`${iban.Iban}`}
          </Base>
          <Base
            className={twMerge(
              "overflow-hidden text-ellipsis break-normal font-semibold text-gray-500 md:mr-3"
            )}
            title={`BIC ${iban.BIC}`}
          >
            {`BIC ${iban.BIC}`}
          </Base>

          <div className={"flex w-fit items-center justify-between"}>
            <Base className={twMerge("mr-1 text-slate-500 text-sm")}>
              {t("atoms.card.verify")} -
            </Base>

            <div className={twMerge("flex items-center")}>
              <Base className={twMerge("mr-1 w-max text-sm underline", "text-slate-500")}>
                {iban?.valid
                  ? t("atoms.iban_card.is_verified")
                  : t("atoms.iban_card.is_not_verified")}
              </Base>

              <Tooltip />
              <div
                data-tooltip={
                  iban?.valid
                    ? t("atoms.iban_card.is_valid", { name: "" })
                    : t("atoms.iban_card.is_invalid", { name: "" })
                }
                data-position="bottom"
              >
                {iban?.valid && <ShieldCheckIcon className={`h-4 w-4 text-green-500`} />}
                {!iban?.valid && onClickHandleVerify && (
                  <Button
                    theme={Themes.white}
                    onClick={() => onClickHandleVerify(iban.Iban)}
                    size="sm"
                    disabled={isDisabledValidationButton || disabled}
                    loading={loading}
                    icon={<ShieldCheckIcon />}
                    iconPosition="after"
                    data-cy="verify_alias"
                  >
                    {t("atoms.iban_card.check")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      </AliasCard>
    </>
  );
};
